import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { PagesID } from "@utils/utilsConfigurations";
import { ServicesURL, downloadBlobFile, onErrorMessage } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import WarningDialogCst from "@components/WarningDialogCst";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { closeUsersAndGroupsModal } from "../store";
import {
  markGroupEnrollmentRemoved,
  useRemovedGroupIds,
} from "../removedGroupsStore";
import EnrollmentListShell from "../components/EnrollmentListShell";
import EnrollmentHistoryModal from "../components/EnrollmentHistoryModal";
import EditEnrollmentModal, {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "../components/EditEnrollmentModal";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import { EnrollType } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import UserEnrollmentRow from "../components/UserEnrollmentRow";
// import SolicitConfirmDialog, {
// SolicitTarget,
// } from "../components/SolicitConfirmDialog";
import RefreshCertificatesDialog, {
  RefreshCertificatesTarget,
} from "../components/RefreshCertificatesDialog";
import { useEnrolledUsersList } from "../hooks/useEnrolledUsersList";
import { getIssuedCertificateIds } from "../hooks/useRefreshCertificates";
import { EnrolledUserItem } from "../types";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import EnrollmentProgressModal from "@pages/Contents/V2/Corporate/Catalog/EnrollmentProgress/EnrollmentProgressModal";
import { EnrollmentProgressTrigger } from "@pages/Contents/V2/Corporate/Catalog/EnrollmentProgress/types";
import MassiveStatusUploadAction from "@pages/Contents/V2/Corporate/Catalog/EnrollmentProgress/MassiveStatusUploadAction";
import { ActivityV1Type } from "../../AddActivity/types";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

const PAGE_SIZE = 10;

type InheritedParentTypeKey = "course" | "path";

interface InheritedParent {
  id: number;
  typeKey: InheritedParentTypeKey | null;
  title: string | null;
}

const INHERITED_PARENT_TYPE_KEYS: Record<string, InheritedParentTypeKey> = {
  COURSE: "course",
  PATH: "path",
};

const getInheritedParent = (
  user: EnrolledUserItem,
  objectId: number,
): InheritedParent | null => {
  const learningObject = user.learningObject;
  const parentId = Number(learningObject?.blendedParentId);
  if (!Number.isFinite(parentId) || parentId <= 0) return null;
  if (parentId === Number(objectId)) return null;
  const rawType = String(learningObject?.blendedParentType ?? "").toUpperCase();
  const rawTitle = learningObject?.blendedParentTitle;
  return {
    id: parentId,
    typeKey: INHERITED_PARENT_TYPE_KEYS[rawType] ?? null,
    title:
      typeof rawTitle === "string" && rawTitle.trim() ? rawTitle.trim() : null,
  };
};

interface EnrolledUsersTabProps {
  corporateId: string | number;
  objectId: number;
  objectType: CatalogEntityType;
  activityType?: ActivityV1Type;
  objectTitle?: string;
  onChange?: () => void;
}

const EnrolledUsersTab: React.FC<EnrolledUsersTabProps> = ({
  corporateId,
  objectId,
  objectType,
  objectTitle,
  activityType,
  onChange,
}) => {
  const { t } = useTranslation("users-and-groups");
  const { t: tInput } = useTranslation("input");
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canUpdateEnrollment = can(PERMISSIONS.enrollment.update);
  const canDownloadCertificate = can(PERMISSIONS.certificate.issuedRead);
  // const canSolicit = can(PERMISSIONS.enrollment.reminder);
  const canMonitorProgress = can(PERMISSIONS.enrollment.statusMonitor);
  const canMassiveUpdateStatus = can(
    PERMISSIONS.enrollment.statusMassiveUpdate,
  );

  const enrollTypeOptions = [
    "top_mandatory",
    "top",
    "pre_auto",
    "pre_bottom",
  ].map((key) => ({
    value: key.toUpperCase(),
    label: String(tInput(`options.enroll_type.${key}`)),
  }));
  const statusOptions = ["e", "c", "p", "t", "x"].map((key) => ({
    value: key.toUpperCase(),
    label: String(tInput(`options.status_enrollment.${key}`)),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [deleteTarget, setDeleteTarget] = useState<{
    user: EnrolledUserItem;
    scope: "single" | "group";
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<EnrolledUserItem | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<EnrolledUserItem | null>(null);
  const [editGroupTarget, setEditGroupTarget] =
    useState<EnrolledUserItem | null>(null);
  const [groupEnrollSource, setGroupEnrollSource] =
    useState<EnrolledUserItem | null>(null);
  // const [solicitTarget, setSolicitTarget] = useState<SolicitTarget | null>(
  // null,
  // );
  const [alertDialog, setAlertDialog] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [progressTrigger, setProgressTrigger] =
    useState<EnrollmentProgressTrigger | null>(null);
  const [refreshCertificatesTarget, setRefreshCertificatesTarget] =
    useState<RefreshCertificatesTarget | null>(null);

  const {
    items: fetchedItems,
    totalItems: fetchedTotalItems,
    totalPages: fetchedTotalPages,
    isLoading,
    mutate,
  } = useEnrolledUsersList({
    corporateId,
    objectId,
    page,
    pageSize,
    search: search || undefined,
    fieldFilters,
  });

  const removedGroupIds = useRemovedGroupIds(corporateId, objectId);
  const items = removedGroupIds.size
    ? fetchedItems.filter(
        (user) =>
          user.massiveGroupId == null ||
          !removedGroupIds.has(user.massiveGroupId) ||
          user.status === "C",
      )
    : fetchedItems;
  const hiddenCount = fetchedItems.length - items.length;
  const totalItems = Math.max(fetchedTotalItems - hiddenCount, 0);
  const totalPages = hiddenCount
    ? Math.max(Math.ceil(totalItems / pageSize), 1)
    : fetchedTotalPages;

  const refresh = () => {
    mutate();
    onChange?.();
  };

  const certificatesAvailable = items.some(
    (user) => getIssuedCertificateIds(user).length > 0,
  );

  const openRefreshUserCertificates = (user: EnrolledUserItem) => {
    if (!user.userId) return;
    setRefreshCertificatesTarget({
      scope: "user",
      user,
      name:
        [user.userFirstName, user.userLastName]
          .filter(Boolean)
          .join(" ")
          .trim() || "-",
    });
  };

  const goToUserDetail = (user: EnrolledUserItem) => {
    if (!user.userId) return;
    closeUsersAndGroupsModal();
    navigate(`/v2/corporate/${corporateId}/detail/users-groups/${user.userId}`);
  };

  const openInheritedEnrollmentAlert = (parent: InheritedParent) => {
    const description =
      parent.typeKey && parent.title
        ? String(
            t("card.inherited-enrollment.description", {
              type: t(`card.inherited-enrollment.type.${parent.typeKey}`),
              name: parent.title,
              id: parent.id,
              interpolation: { escapeValue: false },
            }),
          )
        : String(
            t("card.inherited-enrollment.description-fallback", {
              id: parent.id,
            }),
          );
    setAlertDialog({
      title: String(t("card.delete-error.title")),
      description,
    });
  };

  const requestDeleteEnrollment = (user: EnrolledUserItem) => {
    const parent = getInheritedParent(user, objectId);
    if (parent) {
      openInheritedEnrollmentAlert(parent);
      return;
    }
    setDeleteTarget({ user, scope: "single" });
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !canUpdateEnrollment) return;
    const { user, scope } = deleteTarget;
    if (scope === "single" && !user.enrollId) return;
    if (scope === "group" && (user.massiveGroupId == null || !user.groupType))
      return;
    setIsDeleting(true);
    try {
      const url =
        scope === "group"
          ? `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${user.massiveGroupId}&groupType=${user.groupType}&objectId=${objectId}`
          : `${ServicesURL.deleteEnrollment}?enrollmentEId=${user.enrollId}`;
      await fetcher(url, MethodType.DELETE);
      if (scope === "group" && user.massiveGroupId != null)
        markGroupEnrollmentRemoved(corporateId, objectId, user.massiveGroupId);
      setDeleteTarget(null);
      refresh();
      openToast({
        severity: "success",
        message: String(t("card.delete-confirm.success")),
      });
    } catch (error: any) {
      console.error(error);
      setDeleteTarget(null);
      const apiMessage = onErrorMessage(error);
      setAlertDialog({
        title: String(t("card.delete-error.title")),
        description:
          error?.response?.data?.message ||
          (apiMessage !== error?.message && apiMessage) ||
          String(t("card.delete-confirm.error")),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadCertificate = async (user: EnrolledUserItem) => {
    const certificateIds = user.certificateIds ?? [];
    if (!canDownloadCertificate || certificateIds.length === 0 || !user.userId)
      return;
    try {
      if (certificateIds.length === 1) {
        const url = `${ServicesURL.getIssuedCertificate.replace(
          ":certificateId",
          String(certificateIds[0]),
        )}?objectId=${objectId}&learnerId=${user.userId}`;
        await downloadBlobFile(
          url,
          null,
          `issued_certificate_lo_${objectId}_learner_${user.userId}.pdf`,
          MethodType.GET,
        );
      } else {
        // Piu' certificati sullo stesso LO: download unico come zip
        await downloadBlobFile(
          ServicesURL.getIssuedCertificatesZip,
          {
            issuedCertificates: certificateIds.map((certificateId) => ({
              learnerId: user.userId,
              objectId,
              issuedCertificateId: certificateId,
            })),
          },
          `issued_certificates_lo_${objectId}_learner_${user.userId}.zip`,
        );
      }
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("card.certificate-error")),
      });
    }
  };

  // const openSolicit = (user: EnrolledUserItem) => {
  // if (!user.userId) return;
  // const name =
  // [user.userFirstName, user.userLastName]
  // .filter(Boolean)
  // .join(" ")
  // .trim() || "-";
  // setSolicitTarget({
  // scope: "user",
  // name,
  // corporateId,
  // objectId,
  // objectType,
  // userId: user.userId,
  // });
  // };

  const openProgress = (user: EnrolledUserItem) => {
    if (!user.userId) return;
    setProgressTrigger({
      corporateId,
      userId: user.userId,
      userName:
        [user.userFirstName, user.userLastName]
          .filter(Boolean)
          .join(" ")
          .trim() || undefined,
      objectId,
      objectType,
      objectTitle,
      category: activityType,
      status: user.status,
      completeDate: user.completeDate,
      progPerc: user.progPerc,
      learningObjectId: user.learningObject?.idLearningObject ?? null,
      forceable: user.forceable,
      onSuccess: refresh,
      source: "enrolled-users",
    });
  };

  const deleteTargetName = deleteTarget
    ? [deleteTarget.user.userFirstName, deleteTarget.user.userLastName]
        .filter(Boolean)
        .join(" ")
        .trim() || "-"
    : "";

  return (
    <EnrollmentListShell
      search={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      hideSearch
      actionsSlot={
        <>
          <MassiveStatusUploadAction
            corporateId={corporateId}
            objectId={objectId}
            onUploaded={refresh}
            disabled={!canMassiveUpdateStatus}
          />
          <ButtonTertiary
            size="sm"
            endIcon={<RotateCcw size={14} />}
            onClick={() => setRefreshCertificatesTarget({ scope: "all" })}
            disabled={!canUpdateEnrollment || !certificatesAvailable}
            sx={{
              textDecoration: "none",
              "&:hover": { textDecoration: "none" },
            }}
          >
            {t("card.refresh-certificates.action")}
          </ButtonTertiary>
        </>
      }
      filtersPageId={PagesID["corporate.catalog.courses.subscribed"]}
      filtersExcludeKeys={["blendedParentId"]}
      filtersDynamicOptions={{
        enrollType: enrollTypeOptions,
        status: statusOptions,
      }}
      fieldFilters={fieldFilters}
      onApplyFilters={(next) => {
        setFieldFilters(next);
        setPage(1);
      }}
      isLoading={isLoading}
      isEmpty={items.length === 0}
      emptyLabel={String(t("empty.enrolled-users"))}
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
    >
      {items.map((user) => (
        <UserEnrollmentRow
          key={`${user.userId}-${user.enrollId ?? ""}`}
          user={user}
          onDetails={goToUserDetail}
          activityType={activityType}
          showExpirationBadge
          onEditEnrollment={(u) => setEditTarget(u)}
          onEditGroupEnrollment={(u) => setEditGroupTarget(u)}
          onDeleteEnrollment={requestDeleteEnrollment}
          onDeleteGroupEnrollment={(u) =>
            setDeleteTarget({ user: u, scope: "group" })
          }
          onHistory={(u) => setHistoryTarget(u)}
          onDownloadCertificate={downloadCertificate}
          onRefreshCertificate={openRefreshUserCertificates}
          // onSolicit={openSolicit}
          onViewProgress={openProgress}
          // S05: azioni sempre previste, disabilitate senza permesso.
          // Storico con update.enrollment.write (P70).
          disabledActions={{
            edit: !canUpdateEnrollment,
            editGroup: !canUpdateEnrollment,
            delete: !canUpdateEnrollment,
            deleteGroup: !canUpdateEnrollment,
            history: !canUpdateEnrollment,
            certificate: !canDownloadCertificate,
            refreshCertificate: !canUpdateEnrollment,
            // solicit: !canSolicit,
            progress: !canMonitorProgress,
          }}
        />
      ))}

      {/* <SolicitConfirmDialog
        target={solicitTarget}
        onClose={() => setSolicitTarget(null)}
      /> */}

      <EnrollmentProgressModal
        trigger={progressTrigger}
        onClose={() => setProgressTrigger(null)}
      />

      <RefreshCertificatesDialog
        target={refreshCertificatesTarget}
        corporateId={corporateId}
        objectId={objectId}
        onClose={() => setRefreshCertificatesTarget(null)}
        onSuccess={refresh}
      />

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(
          t(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.title"
              : "card.delete-confirm.title",
          ),
        )}
        description={String(
          t(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.description"
              : "card.delete-confirm.description",
            { name: deleteTargetName },
          ),
        )}
        confirmLabel={String(t("card.delete-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <WarningDialogCst
        open={!!alertDialog}
        title={alertDialog?.title ?? ""}
        description={alertDialog?.description ?? ""}
        closeLabel={String(t("card.delete-error.close"))}
        onClose={() => setAlertDialog(null)}
      />

      <EnrollmentHistoryModal
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        objectId={objectId}
        objectTitle={objectTitle}
        user={historyTarget}
      />

      <EditEnrollmentModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        corporateId={corporateId}
        objectId={objectId}
        objectType={objectType}
        objectTitle={objectTitle}
        user={editTarget}
        onSuccess={refresh}
      />

      <ConfirmDialogCst
        open={!!editGroupTarget}
        title={String(t("card.edit-group-confirm.title"))}
        description={String(
          t("card.edit-group-confirm.description", {
            groupId: editGroupTarget?.massiveGroupId ?? "-",
          }),
        )}
        confirmLabel={String(t("card.edit-group-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        onClose={() => setEditGroupTarget(null)}
        onConfirm={() => {
          setGroupEnrollSource(editGroupTarget);
          setEditGroupTarget(null);
        }}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={
          groupEnrollSource
            ? {
                id: groupEnrollSource.massiveGroupId,
                name:
                  groupEnrollSource.groupName ||
                  `ID ${groupEnrollSource.massiveGroupId}`,
              }
            : null
        }
        groupType={groupEnrollSource?.groupType ?? "STATIC"}
        corporateId={String(corporateId)}
        onSuccess={refresh}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: objectId,
                  kind: objectType,
                  title: objectTitle ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource
            ? buildConfigFromEnrollment(groupEnrollSource)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          groupEnrollSource?.enrollType as EnrollType | undefined,
        )}
      />
    </EnrollmentListShell>
  );
};

export default EnrolledUsersTab;
